import { Component, EventEmitter, Input, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import SignaturePad from 'signature_pad';
import { I18nService } from '../../../services/i18n.service';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { AuthService } from '../../../services/auth.service';
import { SignatureData, SignerInfo } from '../../../models/signature.model';

@Component({
  selector: 'app-signature-modal',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <!-- 全螢幕 Modal -->
    <div class="modal-backdrop" [class.show]="isModalOpen"></div>
    <div class="modal" [class.show]="isModalOpen" tabindex="-1" role="dialog">
      <div class="modal-dialog modal-fullscreen" role="document">
        <div class="modal-content">
          <!-- Modal Header -->
          <div class="modal-header">
            <h5 class="modal-title">{{ 'form.signature.fullscreen_title' | translate }}</h5>
            <div class="modal-header-actions">
              <button type="button" class="btn btn-outline-secondary me-2" (click)="clearSignature()">
                <i class="fas fa-eraser"></i> {{ 'form.signature.clear_button' | translate }}
              </button>
              <button type="button" class="btn btn-outline-dark" (click)="closeModal()">
                <i class="fas fa-times"></i> {{ 'common.close' | translate }}
              </button>
            </div>
          </div>

          <!-- Modal Body -->
          <div class="modal-body">
            <div class="signature-fullscreen-container">
              <canvas #signatureCanvas class="signature-fullscreen-canvas"></canvas>
              <div class="signature-hint" *ngIf="signaturePad && isSignatureEmpty">
                <i class="fas fa-signature"></i>
                <p>{{ 'form.signature.fullscreen_hint' | translate }}</p>
              </div>
            </div>
          </div>

          <!-- Modal Footer -->
          <div class="modal-footer">
            <div class="signer-info" *ngIf="currentUser">
              <span class="text-muted">{{ 'form.signature.signer' | translate }}: {{ currentUser.username }}</span>
            </div>
            <!-- 除錯資訊 -->
            <!-- <div class="debug-info" style="font-size: 12px; color: #666; margin-right: 10px;">
              signaturePad: {{ !!signaturePad }},
              isEmpty: {{ isSignatureEmpty }},
              disabled: {{ !signaturePad || isSignatureEmpty }}
            </div> -->
            <button type="button" class="btn btn-success"
                    [disabled]="!signaturePad || isSignatureEmpty"
                    (click)="saveSignature()">
              <i class="fas fa-check"></i> {{ 'form.signature.save_button' | translate }}
            </button>
            <!-- 測試按鈕 -->
            <!-- <button type="button" class="btn btn-warning ms-2"
                    (click)="testSaveSignature()">
              測試儲存
            </button> -->
          </div>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .modal-backdrop {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.5);
      z-index: 9998;
      opacity: 0;
      transition: opacity 0.3s ease;
      display: none;
    }

    .modal-backdrop.show {
      opacity: 1;
      display: block;
    }

    .modal {
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 9999;
      display: none;
      align-items: center;
      justify-content: center;
    }

    .modal.show {
      display: flex;
    }

    .modal-dialog {
      width: 100%;
      height: 100%;
      margin: 0;
      max-width: none;
    }

    .modal-fullscreen {
      width: 100vw;
      height: 100vh;
    }

    .modal-content {
      width: 100%;
      height: 100%;
      border: none;
      border-radius: 0;
      display: flex;
      flex-direction: column;
    }

    .modal-header {
      border-bottom: 1px solid #dee2e6;
      padding: 1rem 1.5rem;
      flex-shrink: 0;
    }

    .modal-header-actions {
      display: flex;
      align-items: center;
    }

    .modal-body {
      padding: 0;
      position: relative;
      height: calc(100vh - 120px);
    }

    .signature-fullscreen-container {
      width: 100%;
      height: 100%;
      position: relative;
      background-color: #fff;
      border: 2px dashed #dee2e6;
    }

    .signature-fullscreen-canvas {
      width: 100%;
      height: 100%;
      cursor: crosshair;
      touch-action: none;
      display: block;
    }

    .signature-hint {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      color: #6c757d;
      pointer-events: none;
      z-index: 1;
    }

    .signature-hint i {
      font-size: 3rem;
      margin-bottom: 1rem;
      display: block;
    }

    .signature-hint p {
      font-size: 1.2rem;
      margin: 0;
    }

    .modal-footer {
      border-top: 1px solid #dee2e6;
      padding: 1rem 1.5rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .signer-info {
      flex: 1;
    }

    .btn {
      padding: 0.5rem 1rem;
      font-weight: 500;
    }

    .btn i {
      margin-right: 0.5rem;
    }

    @media (max-width: 768px) {
      .modal-header {
        padding: 0.75rem 1rem;
      }

      .modal-footer {
        padding: 0.75rem 1rem;
        flex-direction: column;
        gap: 0.5rem;
      }

      .signer-info {
        text-align: center;
        width: 100%;
      }

      .signature-hint i {
        font-size: 2rem;
      }

      .signature-hint p {
        font-size: 1rem;
      }
    }
  `]
})
export class SignatureModalComponent implements AfterViewInit, OnDestroy {
  @Input() initialSignature: SignatureData | null = null;
  @Output() signatureSaved = new EventEmitter<SignatureData>();
  @Output() modalClosed = new EventEmitter<void>();

  @ViewChild('signatureCanvas') signatureCanvasRef!: ElementRef<HTMLCanvasElement>;

  signaturePad!: SignaturePad;
  currentUser: any;
  isModalOpen: boolean = false;
  private resizeObserver: ResizeObserver | null = null;

  // 添加一個手動狀態追蹤
  public isSignatureEmpty: boolean = true;

  constructor(
    private i18nService: I18nService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef
  ) {
    this.currentUser = this.authService.getUserInfo();
  }

  ngAfterViewInit() {
    this.initializeCanvas();
    this.setupResizeObserver();

    // 如果有初始簽名，載入它
    if (this.initialSignature?.imageData) {
      this.loadSignature(this.initialSignature.imageData);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  private initializeCanvas() {
    if (!this.signatureCanvasRef) return;

    const canvas = this.signatureCanvasRef.nativeElement;
    this.resizeCanvas();

    this.signaturePad = new SignaturePad(canvas, {
      backgroundColor: 'rgba(255, 255, 255, 0)',
      penColor: '#000000',
      minWidth: 2,
      maxWidth: 4,
      throttle: 16,
      minDistance: 5
    });

    // 監聽簽名事件
    this.signaturePad.addEventListener('beginStroke', () => {
      // console.log('開始簽名');
      // 簽名開始時可以執行一些動作
    });

    this.signaturePad.addEventListener('endStroke', () => {
      // console.log('結束簽名，isEmpty:', this.signaturePad.isEmpty());

      // 手動更新狀態並觸發變更檢測
      this.isSignatureEmpty = this.signaturePad.isEmpty();
      this.cdr.detectChanges();

      // console.log('手動更新 isSignatureEmpty:', this.isSignatureEmpty);

      // 簽名結束時強制觸發變更檢測
      setTimeout(() => {
        // console.log('延遲檢查 isEmpty:', this.signaturePad.isEmpty());
        this.isSignatureEmpty = this.signaturePad.isEmpty();
        this.cdr.detectChanges();
      }, 100);
    });
  }

  private setupResizeObserver() {
    if (typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeCanvas();
      });

      const container = this.signatureCanvasRef.nativeElement.parentElement;
      if (container) {
        this.resizeObserver.observe(container);
      }
    }
  }

  private resizeCanvas() {
    if (!this.signatureCanvasRef) return;

    const canvas = this.signatureCanvasRef.nativeElement;
    const container = canvas.parentElement;

    if (container) {
      const rect = container.getBoundingClientRect();

      // 先保存現有簽名（如果有的話）
      let existingSignature: string | null = null;
      if (this.signaturePad && !this.signaturePad.isEmpty()) {
        existingSignature = this.signaturePad.toDataURL();
        // console.log('保存現有簽名，準備重新調整畫布大小');
      }

      // 設定畫布的實際尺寸
      canvas.width = rect.width;
      canvas.height = rect.height;

      // 重新初始化 SignaturePad（如果已存在）
      if (this.signaturePad) {
        // 如果有保存的簽名，重新載入
        if (existingSignature) {
          // console.log('重新載入保存的簽名');
          this.loadSignature(existingSignature);
        } else if (this.initialSignature?.imageData) {
          // 如果沒有現有簽名但有初始簽名，載入初始簽名
          this.loadSignature(this.initialSignature.imageData);
        }
      }
    }
  }

  private loadSignature(imageData: string) {
    if (!this.signaturePad || !imageData) return;

    const image = new Image();
    image.onload = () => {
      const canvas = this.signatureCanvasRef.nativeElement;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      }
    };
    image.src = imageData;
  }

  clearSignature() {
    if (this.signaturePad) {
      this.signaturePad.clear();
      this.isSignatureEmpty = true;
      this.cdr.detectChanges();
      console.log('清除簽名，isSignatureEmpty:', this.isSignatureEmpty);
    }
  }

  saveSignature() {
    // console.log('=== SignatureModal saveSignature 開始 ===');
    // console.log('signaturePad:', this.signaturePad);
    // console.log('isSignatureEmpty:', this.isSignatureEmpty);
    // console.log('signaturePad.isEmpty():', this.signaturePad?.isEmpty());

    if (!this.signaturePad || this.isSignatureEmpty) {
      console.log('簽名為空或 signaturePad 不存在，返回');
      return;
    }

    const imageData = this.signaturePad.toDataURL('image/png');
    // console.log('生成的 imageData 長度:', imageData.length);

    const signerInfo: SignerInfo = {
      userId: this.currentUser.userid,
      username: this.currentUser.username,
      companyId: this.currentUser.companyid,
      signedAt: new Date().toISOString()
    };

    const signatureData: SignatureData = {
      imageData,
      signerInfo
    };

    // console.log('準備發出 signatureSaved 事件，資料為:', signatureData);
    this.signatureSaved.emit(signatureData);
    // console.log('已發出 signatureSaved 事件');

    this.closeModal();
    // console.log('=== SignatureModal saveSignature 結束 ===');
  }

  testSaveSignature() {
    console.log('=== 測試儲存按鈕被點擊 ===');
    console.log('signaturePad:', this.signaturePad);
    console.log('signaturePad.isEmpty():', this.signaturePad?.isEmpty());

    // 強制觸發儲存，即使簽名為空
    if (this.signaturePad) {
      const imageData = this.signaturePad.toDataURL('image/png');
      console.log('測試生成的 imageData 長度:', imageData.length);

      const testSignatureData: SignatureData = {
        imageData: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==', // 最小測試圖片
        signerInfo: {
          userId: this.currentUser?.userid || 'test',
          username: this.currentUser?.username || 'test',
          companyId: this.currentUser?.companyid || 'test',
          signedAt: new Date().toISOString()
        }
      };

      console.log('發出測試 signatureSaved 事件');
      this.signatureSaved.emit(testSignatureData);
    }
  }

  closeModal() {
    this.isModalOpen = false;
    this.modalClosed.emit();
  }

  openModal() {
    this.isModalOpen = true;
    // 等待 DOM 更新後初始化畫布
    setTimeout(() => {
      this.resizeCanvas();
    }, 100);
  }
}
